/**
 * Auth Service - API Functions
 * Pure API calls without React dependencies
 */

import { post, get } from '@/services/shared/client'
import * as mockAuth from './mock'
import { isBackendMockEnabled } from '@/utils/env'
import type {
  User,
  SendOTPRequest,
  SendOTPResponse,
  VerifyOTPRequest,
  LoginResponse,
} from '@/types/api'

// ==================== Email OTP Types ====================

export interface SendEmailOTPRequest {
  email: string
}

export interface SendEmailOTPResponse {
  success: boolean
  message: string
  data: {
    email: string
    expiresIn: number // Minutes until OTP expires
  }
}

export interface VerifyEmailOTPRequest {
  email: string
  otp: string
  platform?: 'web' | 'ios' | 'android'
  language?: 'en' | 'ko'
  theme?: 'light' | 'dark' | 'system'
  timezone?: string
  rememberMe?: boolean
}

// ==================== API Functions ====================

export async function sendOTP(data: SendOTPRequest): Promise<SendOTPResponse> {
  if (isBackendMockEnabled()) {
    return mockAuth.sendOTP(data)
  }
  return post<SendOTPResponse>('/auth/send-otp', data)
}

export async function sendEmailOTP(
  data: SendEmailOTPRequest
): Promise<SendEmailOTPResponse> {
  if (isBackendMockEnabled()) {
    // Mock implementation for development
    return {
      success: true,
      message: 'Verification code sent successfully',
      data: {
        email: data.email,
        expiresIn: 5,
      },
    }
  }
  return post<SendEmailOTPResponse>('/auth/send-email-otp', data)
}

export async function verifyEmailOTP(
  data: VerifyEmailOTPRequest
): Promise<LoginResponse> {
  if (isBackendMockEnabled()) {
    // Mock implementation for development
    throw new Error('verifyEmailOTP not implemented in mock API')
  }
  return post<LoginResponse>('/auth/verify-email-otp', data)
}

/**
 * Google OAuth Login
 */
export interface GoogleLoginRequest {
  idToken: string
  platform?: 'web' | 'ios' | 'android'
  language?: 'en' | 'ko'
  theme?: 'light' | 'dark' | 'system'
  timezone?: string
  rememberMe?: boolean
}

export async function googleLogin(
  data: GoogleLoginRequest
): Promise<LoginResponse> {
  if (isBackendMockEnabled()) {
    throw new Error('Google login not implemented in mock API')
  }
  return post<LoginResponse>('/auth/google', data)
}

export async function verifyOTP(
  data: VerifyOTPRequest
): Promise<LoginResponse> {
  if (isBackendMockEnabled()) {
    return mockAuth.verifyOTP(data)
  }
  return post<LoginResponse>('/auth/verify-otp', data)
}

export async function logout(
  refreshToken?: string,
  logoutAll?: boolean
): Promise<void> {
  if (isBackendMockEnabled()) {
    return mockAuth.logout(refreshToken, logoutAll)
  }
  return post<void>(
    '/auth/logout',
    { refreshToken, logoutAll },
    {
      // Keep request alive during immediate navigation/unload so server-side
      // refresh-cookie invalidation has the highest chance to complete.
      keepalive: true,
    }
  )
}

export async function getCurrentUser(): Promise<User> {
  if (isBackendMockEnabled()) {
    // Mock API doesn't support getCurrentUser yet, return null
    throw new Error('getCurrentUser not implemented in mock API')
  }
  const response = await get<{ success: boolean; message: string; data: User }>(
    '/auth/me'
  )
  return response.data
}

export async function requestPasswordReset(email: string): Promise<void> {
  if (isBackendMockEnabled()) {
    throw new Error('Password reset not implemented in mock API')
  }
  return post<void>('/auth/password-reset', { email })
}

export async function resetPassword(
  token: string,
  password: string
): Promise<void> {
  if (isBackendMockEnabled()) {
    throw new Error('Password reset not implemented in mock API')
  }
  return post<void>('/auth/password-reset/confirm', { token, password })
}

export async function refreshToken(
  refreshToken?: string
): Promise<{ accessToken: string }> {
  if (isBackendMockEnabled()) {
    return mockAuth.refreshToken(refreshToken || '')
  }
  // For web: refreshToken is undefined, backend uses httpOnly cookie
  // For mobile: refreshToken is passed in body
  const response = await post<{
    success: boolean
    message: string
    data: { accessToken: string }
  }>('/auth/refresh', refreshToken ? { refreshToken } : undefined)
  return response.data
}

/**
 * Validate Session
 *
 * Validates the current session using the httpOnly refreshToken cookie.
 * Used during hydration on protected routes to check if a cookie session exists
 * when localStorage state is out of sync.
 *
 * ALWAYS returns 200 OK to avoid triggering error monitoring:
 * - No cookie: returns { success: true, authenticated: false }
 * - Invalid/expired token: clears cookie, returns { success: true, authenticated: false }
 * - Valid session: returns { success: true, authenticated: true, data: { user, accessToken } }
 *
 * The `authenticated` boolean is the source of truth, not HTTP status codes.
 */
export interface ValidateSessionResponse {
  success: boolean
  authenticated: boolean
  message?: string
  data?: {
    user: User
    accessToken: string
  }
}

export async function validateSession(): Promise<ValidateSessionResponse> {
  if (isBackendMockEnabled()) {
    throw new Error('validateSession not implemented in mock API')
  }
  // skipAuth = true because this endpoint uses the httpOnly refreshToken cookie,
  // not the Authorization header. This is used to restore sessions when
  // localStorage is cleared but the cookie still exists.
  return get<ValidateSessionResponse>('/auth/validate', true)
}
